/**
 * Analytics Service
 *
 * Utility for tracking user interactions with lists, profiles, and places.
 * All tracking is fire-and-forget (non-blocking).
 */

import api from './api';

// Event types - must match backend EVENT_TYPES
export const EVENT_TYPES = {
  // List events
  LIST_VIEW: 'list_view',
  LIST_FOLLOW: 'list_follow',
  LIST_UNFOLLOW: 'list_unfollow',
  LIST_DUPLICATE: 'list_duplicate',
  PLACE_CLICK: 'place_click',
  PLACE_SAVE: 'place_save',
  LIST_LINK_CLICK: 'list_link_click',

  // Profile events
  PROFILE_VIEW: 'profile_view',
  LINK_CLICK: 'link_click',
  ACCOUNT_FOLLOW: 'account_follow',
  ACCOUNT_UNFOLLOW: 'account_unfollow',
  LIST_CLICK: 'list_click',
};

// Target types - must match backend TARGET_TYPES
export const TARGET_TYPES = {
  LIST: 'list',
  PROFILE: 'profile',
  PLACE: 'place',
};

// Generate a session ID for anonymous tracking deduplication
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track an analytics event (fire and forget)
 * @param {Object} params
 * @param {string} params.eventType - Event type from EVENT_TYPES
 * @param {string} params.targetId - ID of the target (listId, userId, placeId)
 * @param {string} params.targetType - Target type from TARGET_TYPES
 * @param {string} params.ownerId - User ID of the content owner
 * @param {Object} params.metadata - Additional context
 */
export const trackEvent = async ({
  eventType,
  targetId,
  targetType,
  ownerId,
  metadata = {},
}) => {
  try {
    await api.trackAnalyticsEvent({
      eventType,
      targetId,
      targetType,
      ownerId,
      metadata,
    });
  } catch (error) {
    // Silent fail - analytics should never break the app
    console.debug('Analytics tracking failed:', error.message);
  }
};

/**
 * Track a public profile view (for anonymous users on /c/:username)
 * @param {string} userId - User ID of the profile owner
 */
export const trackPublicProfileView = async (userId) => {
  try {
    await api.trackPublicProfileView({
      targetId: userId,
      ownerId: userId,
      metadata: {
        sessionId: getSessionId(),
      },
    });
  } catch (error) {
    console.debug('Public analytics tracking failed:', error.message);
  }
};

// ==================== Convenience Methods ====================

/**
 * Track a list view
 * @param {string} listId - ID of the list being viewed
 * @param {string} ownerId - User ID of the list owner
 */
export const trackListView = (listId, ownerId) => {
  trackEvent({
    eventType: EVENT_TYPES.LIST_VIEW,
    targetId: listId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
  });
};

/**
 * Track a list follow
 * @param {string} listId - ID of the list being followed
 * @param {string} ownerId - User ID of the list owner
 */
export const trackListFollow = (listId, ownerId) => {
  trackEvent({
    eventType: EVENT_TYPES.LIST_FOLLOW,
    targetId: listId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
  });
};

/**
 * Track a list unfollow
 * @param {string} listId - ID of the list being unfollowed
 * @param {string} ownerId - User ID of the list owner
 */
export const trackListUnfollow = (listId, ownerId) => {
  trackEvent({
    eventType: EVENT_TYPES.LIST_UNFOLLOW,
    targetId: listId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
  });
};

/**
 * Track a list duplicate
 * @param {string} listId - ID of the list being duplicated
 * @param {string} ownerId - User ID of the list owner
 */
export const trackListDuplicate = (listId, ownerId) => {
  trackEvent({
    eventType: EVENT_TYPES.LIST_DUPLICATE,
    targetId: listId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
  });
};

/**
 * Track a place click from a list
 * @param {string} listId - ID of the list the place is in
 * @param {string} ownerId - User ID of the list owner
 * @param {string} placeId - ID of the place being clicked
 */
export const trackPlaceClick = (listId, ownerId, placeId) => {
  trackEvent({
    eventType: EVENT_TYPES.PLACE_CLICK,
    targetId: listId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
    metadata: { placeId },
  });
};

/**
 * Track a place save (when someone saves a place from another user's list)
 * @param {string} sourceListId - ID of the list the place was saved from
 * @param {string} ownerId - User ID of the source list owner
 * @param {string} placeId - ID of the place being saved
 */
export const trackPlaceSave = (sourceListId, ownerId, placeId) => {
  trackEvent({
    eventType: EVENT_TYPES.PLACE_SAVE,
    targetId: sourceListId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
    metadata: { placeId },
  });
};

/**
 * Track a profile view (for authenticated users)
 * @param {string} userId - User ID of the profile owner
 */
export const trackProfileView = (userId) => {
  trackEvent({
    eventType: EVENT_TYPES.PROFILE_VIEW,
    targetId: userId,
    targetType: TARGET_TYPES.PROFILE,
    ownerId: userId,
  });
};

/**
 * Track a link click from profile (Instagram, website, etc.)
 * @param {string} userId - User ID of the profile owner
 * @param {string} linkType - Type of link (instagram, twitter, website, etc.)
 */
export const trackLinkClick = (userId, linkType) => {
  trackEvent({
    eventType: EVENT_TYPES.LINK_CLICK,
    targetId: userId,
    targetType: TARGET_TYPES.PROFILE,
    ownerId: userId,
    metadata: { linkType },
  });
};

/**
 * Track a list click from profile page
 * @param {string} userId - User ID of the profile owner
 * @param {string} listId - ID of the list being clicked
 */
export const trackListClickFromProfile = (userId, listId) => {
  trackEvent({
    eventType: EVENT_TYPES.LIST_CLICK,
    targetId: userId,
    targetType: TARGET_TYPES.PROFILE,
    ownerId: userId,
    metadata: { listId },
  });
};

/**
 * Track an external link click within a list
 * @param {string} listId - ID of the list containing the link
 * @param {string} ownerId - User ID of the list owner
 */
export const trackListLinkClick = (listId, ownerId) => {
  trackEvent({
    eventType: EVENT_TYPES.LIST_LINK_CLICK,
    targetId: listId,
    targetType: TARGET_TYPES.LIST,
    ownerId,
  });
};

/**
 * Track an account follow
 * @param {string} userId - User ID being followed
 */
export const trackAccountFollow = (userId) => {
  trackEvent({
    eventType: EVENT_TYPES.ACCOUNT_FOLLOW,
    targetId: userId,
    targetType: TARGET_TYPES.PROFILE,
    ownerId: userId,
  });
};

/**
 * Track an account unfollow
 * @param {string} userId - User ID being unfollowed
 */
export const trackAccountUnfollow = (userId) => {
  trackEvent({
    eventType: EVENT_TYPES.ACCOUNT_UNFOLLOW,
    targetId: userId,
    targetType: TARGET_TYPES.PROFILE,
    ownerId: userId,
  });
};

const analyticsService = {
  EVENT_TYPES,
  TARGET_TYPES,
  trackEvent,
  trackPublicProfileView,
  trackListView,
  trackListFollow,
  trackListUnfollow,
  trackListDuplicate,
  trackPlaceClick,
  trackPlaceSave,
  trackListLinkClick,
  trackProfileView,
  trackLinkClick,
  trackListClickFromProfile,
  trackAccountFollow,
  trackAccountUnfollow,
};

export default analyticsService;
